import { Cross } from '@lt/components/icons-react';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Image as ImageComponent, Portal } from '@lt/components';
import { i18n } from '@lt/localization';
import { useShowBannerController } from 'src/hooks/useShowBannerController';
import palmImagePng from 'src/images/palm_2/palm_2.png';
import palmImageWebp from 'src/images/palm_2/palm_2.webp';
import AppInformation from './components/AppInformation';
import QRCodeCanvas from './components/QRCodeCanvas';
import { BUTTON_DATA_TEST_ID, DATA_TEST_ID, KEY_COOKIE } from './constants';

interface DesktopAppBannerProps {
  appLink: string;
  onShow?: () => void;
  getDeeplink?: () => void;
  moveToAppContinuedWeb?: () => void;
  reviewsCountText: string;
  rating: number;
  getStoreData?: () => void;
}
export const DesktopAppBanner = ({
  appLink,
  onShow,
  getDeeplink,
  moveToAppContinuedWeb,
  reviewsCountText,
  rating,
  getStoreData,
}: DesktopAppBannerProps): JSX.Element | null => {
  const { isShowBanner, handleCloseBanner } =
    useShowBannerController(KEY_COOKIE);

  const handleClose = () => {
    handleCloseBanner();
    moveToAppContinuedWeb?.();
  };

  useEffect(() => {
    if (isShowBanner) {
      onShow?.();
      getDeeplink?.();
      getStoreData?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowBanner]);

  if (!appLink || !isShowBanner) {
    return null;
  }
  return (
    <Portal>
      <Container data-testid={DATA_TEST_ID}>
        <QRCode
          text={appLink}
          options={{
            width: 126,
            version: 7,
            margin: 6,
          }}
        />
        <Content>
          <Title>{i18n.t('mobile_apps_banner.title')}</Title>
          <Description>{i18n.t('mobile_apps_banner.description')}</Description>
          <DescriptionNote>
            {i18n.t('mobile_apps_banner.descriptionNote')}
          </DescriptionNote>
          <ApplicationInformation
            rating={rating}
            reviewsCountText={reviewsCountText}
          />
        </Content>
        <StyledButton onClick={handleClose} data-testid={BUTTON_DATA_TEST_ID}>
          <StyledCross />
        </StyledButton>
        <Image src={palmImageWebp} fallback={palmImagePng} alt="" />
      </Container>
    </Portal>
  );
};

const QRCode = styled(QRCodeCanvas)`
  border-radius: 20px;

  margin-right: 16px;
`;

const Container = styled.div`
  position: fixed;
  left: 30px;
  bottom: 30px;
  overflow: hidden;

  width: 561px;
  padding: 20px 20px 20px 16px;

  display: flex;

  background: linear-gradient(93.17deg, #dbf2ff 0%, #d1faff 100%);
  box-shadow: 0px 1px 2px rgba(210, 208, 197, 0.9),
    0px 10px 15px rgba(216, 214, 204, 0.9);
  border-radius: 8px;

  z-index: 500;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 1;
`;

const Title = styled.p`
  font-weight: 700;
  font-size: 22px;
  line-height: 26px;
  color: #17181a;

  margin-bottom: 3px;
`;

const Description = styled.p`
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: #4d4d4d;
  white-space: pre-line;
`;

const DescriptionNote = styled.p`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #919499;
`;

const StyledButton = styled.div`
  position: relative;
  top: 4px;
  width: 16px;
  height: 16px;
  margin-left: auto;

  cursor: pointer;
`;

const StyledCross = styled(Cross)`
  width: 16px;
  height: 16px;
`;

const Image = styled(ImageComponent)`
  position: absolute;
  width: 177px;
  height: 168px;
  right: -40px;
  bottom: -51px;
`;

const ApplicationInformation = styled(AppInformation)`
  margin-top: 12px;
`;
